import { FunctionComponent, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CollapsibleTable from '@Components/CollapsibleTable';
import ReportsLayout from '@Components/ReportsLayout';
import ScrollToTop from '@Components/ScrollToTop';
import { TableHead } from '@Components/Table';
import CommonConstants from '@Constants/common.constants';
import { Icon } from '@Typings/global.types';
import {
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import DialSessionsTableProps from './DialSessionsTable.types';

const DialSessionsTable: FunctionComponent<DialSessionsTableProps> = (
  props: DialSessionsTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  const { heading, tableProps, adminTableProps, ...resProps } = props;

  /** STATES */
  const [searchValue, setSearchValue] = useState<string>('');
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );

  useEffect(() => {
    const searchParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.SEARCH) || '';
    setSearchValue(`${searchParam}`);

    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    setPage(pageNo);
    setRowsPerPage(pageSize);
  }, [location.search]);

  /** EDGE CASE
   * If the user performs an action and there are no more results to show
   * on the particular page then the user is redirected to the previous valid page.
   *
   * Set the pageNo to the previous valid page.
   */
  useEffect(() => {
    const count =
      tableProps?.paginationProps.count ||
      adminTableProps?.paginationProps.count ||
      0;

    const newPageNo = Math.ceil(count / rowsPerPage);

    if (count && newPageNo < page + 1) {
      const pageNoParam = query.set(
        CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
        newPageNo
      );

      changeRoute(pageNoParam);
    }
  }, [
    tableProps?.paginationProps.count,
    adminTableProps?.paginationProps.count,
    page,
    rowsPerPage,
    changeRoute,
  ]);

  /** PAGE HEADING */
  const pageHeading = t('reports.Sessions Summary');

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(searchParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const searchParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(searchParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** ON SEARCH HANDLER
   * Sets query to search text and page No to 1
   */
  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.SEARCH]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);
  };

  /** TABLE --> */
  /** Clears all checkboxes. */
  const clearSelection = () => {
    if (adminTableProps?.setChecked) {
      adminTableProps.setChecked([]);
    }
  };

  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER);
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const tableHeadOnClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          order || CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('reports.Session Name'),
        width: adminTableProps ? 30.5 : 34.9,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_NAME
        ),
        onClick: () =>
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_NAME
          ),
      },
      {
        label: t('reports.Session Date'),
        width: adminTableProps ? 17.2 : 18,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE
        ),
        align: 'right',
        onClick: () => {
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE
          );
        },
      },
      {
        label: t('reports.Total Time'),
        width: 11.6,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.TOTAL_TIME
        ),
        align: 'right',
        onClick: () => {
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.TOTAL_TIME
          );
        },
      },
      {
        label: t('reports.Calls'),
        width: 9,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.CALLS
        ),
        align: 'right',
        onClick: () => {
          tableHeadOnClick(CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.CALLS);
        },
      },
      {
        label: t('reports.Contacted'),
        width: 12.7,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.CONTACTED
        ),
        align: 'right',
        onClick: () => {
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.CONTACTED
          );
        },
      },
      {
        label: t('reports.No Answer'),
        width: 13,
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.NO_ANSWER
        ),
        align: 'right',
        onClick: () => {
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.NO_ANSWER
          );
        },
      },
      {
        label: t('reports.Duration'),
        width: 11.6,
        align: 'right',
      },
    ];
  };

  /** SESSIONS SUMMARY TABLE COMPONENT */
  const getTableComponent = (): ReactNode => {
    if (tableProps) {
      const { rows, paginationProps, ...resTableProps } = tableProps;

      /** PAGINATION HANDLERS --> */
      const rowsPerPageOptions = getRowsPerPageOptions(
        CommonConstants.INITIAL_ROWS_PER_PAGE,
        tableProps?.paginationProps.count
      );

      const TableComponent = (
        <CollapsibleTable
          rows={rows}
          tableHead={getTableHead()}
          paginationProps={{
            page,
            rowsPerPage,
            rowsPerPageOptions,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            ...paginationProps,
          }}
          {...resTableProps}
        />
      );

      return TableComponent;
    }

    return null;
  };

  /** ADMIN SESSIONS SUMMARY COLLAPSIBLE TABLE COMPONENT */
  const getAdminTableComponent = (): ReactNode => {
    if (adminTableProps) {
      const { rows, paginationProps, ...resAdminTableProps } = adminTableProps;

      /** PAGINATION HANDLERS --> */
      const rowsPerPageOptions = getRowsPerPageOptions(
        CommonConstants.INITIAL_ROWS_PER_PAGE,
        adminTableProps?.paginationProps.count
      );

      const AdminTableComponent = (
        <CollapsibleTable
          rows={rows}
          tableHead={getTableHead()}
          paginationProps={{
            page,
            rowsPerPage,
            rowsPerPageOptions,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            ...paginationProps,
          }}
          {...resAdminTableProps}
        />
      );

      return AdminTableComponent;
    }

    return null;
  };
  /** <-- TABLE */

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <ReportsLayout
        isAdmin={!!adminTableProps}
        heading={heading || pageHeading}
        searchProps={{
          value: searchValue,
          setValue: setSearchValue,
          onSearch,
        }}
        selectedBtnProps={
          adminTableProps
            ? {
                onClick: clearSelection,
                text: adminTableProps?.checked?.length,
                disabled: adminTableProps?.loading,
              }
            : {
                onClick: clearSelection,
                // eslint-disable-next-line
                text: tableProps?.checked?.length,
                disabled: tableProps?.loading,
              }
        }
        clearFilterBtnProps={{
          onClick: clearSelection,
          disabled: adminTableProps?.loading,
        }}
        table={adminTableProps ? getAdminTableComponent() : getTableComponent()}
        {...resProps}
      />
    </>
  );
};

export default DialSessionsTable;
